import type * as React from 'react';
import { get } from '@Domain/Api';
import facilityUsersFetched, {
	type FacilityUsersFetchedAction,
	type FacilityUsersFetchedPayload,
} from '@Application/Users/FacilityUsers/Actions/facilityUsersFetched';
import facilityUsersFetchFailed, {
	type FacilityUsersFetchFailedAction,
} from '@Application/Users/FacilityUsers/Actions/facilityUsersFetchFailed';
import type FetchFacilityUsers from '@Application/Users/FacilityUsers/Queries/FetchFacilityUsers';
import extractMessages from '@Infra/FetchLib/ApiError/extractMessages';
import parameters from '../../../parameters';

type Actions = FacilityUsersFetchedAction | FacilityUsersFetchFailedAction;

/**
 * Deliberate homonym of `Application/Users/fetchFacilityUsers.ts`: both call the same route,
 * `GET /api/facilities/{facilityId}/users`, but write to different store locations. This one feeds
 * `facilityUsersStore.users`, dedicated to the beneficiary dossier's agency directory. The other feeds
 * `usersStore.users`, the administration screen's own list, whose `UsersListView` carries `accountId`
 * as its sole ownership key — not `facilityId`. Folding this fetch into that one would let an
 * unfiltered `rows=200` read overwrite the admin screen's filtered, paginated page under a matching
 * `accountId`, and its `stopOn` would treat that unrelated page as already loaded, never refetching it.
 */
// `rows=200` is the server's hard maximum page size — it answers `400` past it rather than truncating
// silently. An agency with more than 200 members would lose the tail of its directory: a real limit to
// flag, never to route around from here.
// No `isActive` filter is sent: the route must answer EVERY member. A deactivated professional stays
// nameable on the cases they used to follow; screens filter out inactive members from assignment
// candidates, never from display.
export default (query: FetchFacilityUsers) => {
	return async (dispatch: React.Dispatch<Actions>): Promise<Actions> => {
		try {
			const payload = await get<FacilityUsersFetchedPayload>(
				`${parameters.api.host}/api/facilities/${query.facilityId}/users?rows=200`,
			);

			const action = facilityUsersFetched(payload, query.facilityId);
			dispatch(action);
			return action;
		} catch (error) {
			const action = facilityUsersFetchFailed(extractMessages(error));
			dispatch(action);
			return Promise.reject(action);
		}
	};
};
